<template>
  <Card class="bg-grey-background">
    <div class="container">
      <div class="grid">
        <Card class="grid-item -col-span-12 -col-span-lg-6 d-flex justify-start align-center flex-column" :class="[{ 'pa-5': isMobile }, { 'px-10 py-10': isNotMobile }]">
          <div class="d-flex align-center justify-space-between gap-40 mb-5">
            <VIcon :icon="strapi.Confirmation.SuccessIcon" color="primary" size="56" />
            <div class="d-flex flex-column">
              <p class="paragraph-large -no-margin">{{ strapi.Confirmation.SuccessTitle }}</p>
              <p class="paragraph-large -no-margin">{{ strapi.Confirmation.SuccessText }}</p>
            </div>
            <Button class="bg-secondary" color="white" href="/products/">{{ strapi.Confirmation.BackToStoreText }}</Button>
          </div>
          <div v-if="cartItems" class="mb-8">
            <VDivider class="mt-5 mb-3" />
            <CartItem v-for="cartItem in cartItems" :key="cartItem.id" :isCheckoutConfirmation="true" :cartItem="cartItem" :loading="loading" hideControls />
          </div>
          <div v-if="orderDetails.order" class="w-100">
            <div class="d-flex justify-space-between">
              <p class="paragraph">{{ strapiCheckoutPayment.Payment.SubtotalLabel }}</p>
              <p class="paragraph">{{ $moneyFormat(orderDetails.paymentSummary.subtotal, orderDetails.order.currency_code) }}</p>
            </div>
            <div v-for="orderItem in orderDetails.itemServices" :key="orderItem" class="d-flex justify-space-between">
              <div class="d-flex">
                <p class="paragraph">{{ orderItem.label }}</p>
                <VDialog v-if="$isNotUndefined(orderItem.dialog)" v-model="orderItem.dialog" max-width="500" width="auto">
                  <template #activator="{ props }">
                    <Button v-bind="props" iconButton icon="fas fa-circle-info" iconSize="small" color="info" aria-label="View order item description" class="hover-dark mt-n2" />
                  </template>
                  <Card class="pa-12">
                    <div v-markdown="orderItem.description" />
                    <template #actions>
                      <Button class="mt-8" variant="outlined" color="secondary" @click="orderItem.dialog = false">Ok</Button>
                    </template>
                  </Card>
                </VDialog>
              </div>
              <p class="paragraph">{{ $moneyFormat(orderItem.totalPrice, orderDetails.order.currency_code) }}</p>
            </div>
            <div v-if="orderDetails.paymentSummary.vat.cost !== 0" class="d-flex justify-space-between">
              <p class="paragraph">{{ strapiCheckoutPayment.Payment.VATLabel }} ({{ orderDetails.paymentSummary.vat.percentage }}%)</p>
              <p class="paragraph">{{ $moneyFormat(orderDetails.paymentSummary.vat.cost, orderDetails.order.currency_code) }}</p>
            </div>
            <VDivider :thickness="2" class="border-opacity-75" />
            <div class="d-flex justify-space-between mt-10">
              <p class="paragraph bold">{{ strapi.Confirmation.TotalSumTitle }}</p>
              <p class="paragraph bold">{{ $moneyFormat(orderDetails.order.order_sum, orderDetails.order.currency_code) }}</p>
            </div>
          </div>
        </Card>
        <Card class="grid-item -col-span-12 -col-span-lg-6" :class="[{ 'pa-5': isMobile }, { 'pa-10': isNotMobile }]">
          <header class="d-flex justify-space-between mt-5">
            <div class="d-flex align-center">
              <VIcon :icon="strapi.Confirmation.ReceiptIcon" size="24" class="mr-3" />
              <p class="paragraph-large -no-margin">{{ strapi.Confirmation.ReceiptTitle }}</p>
            </div>
            <div class="d-flex align-center">
              <p class="paragraph-small -no-margin mr-4">{{ strapi.Confirmation.PrintLabel }}</p>
              <a class="ts-link-underline" target="_blank" :href="`/order/order_confirmation/${orderDetails.order?.id}/`"><VIcon :icon="strapi.Confirmation.PrintIcon" color="primary" size="24" /></a>
            </div>
          </header>
          <VDivider class="mt-5" />
          <header v-if="orderDetails.order" class="mt-5 d-flex flex-column">
            <div class="d-flex align-center mb-5">
              <VIcon icon="fa-solid fa-cart-shopping" size="24" class="mr-3" />
              <p class="paragraph-large -no-margin vertical">{{ strapi.Confirmation.OrderInfoTitle }}</p>
            </div>
            <div class="pl-9 d-flex flex-column gap-16">
              <div class="d-flex justify-space-between">
                <p class="paragraph-small -no-margin w-70">{{ strapi.Confirmation.OrderNumberTitle }}</p>
                <p class="paragraph-small -no-margin w-30">{{ orderDetails.order.number }}</p>
              </div>
              <div class="d-flex justify-space-between">
                <p class="paragraph-small -no-margin w-70">{{ strapi.Confirmation.ShippingAgentTitle }}</p>
                <p class="paragraph-small -no-margin w-30">{{ orderDetails.customerDetails.shippingAgent.name }}</p>
              </div>
              <div class="d-flex justify-space-between">
                <p class="paragraph-small -no-margin w-70">{{ strapi.Confirmation.DeliveryAddressTitle }}</p>
                <div class="d-flex flex-column w-30">
                  <p class="paragraph-small -no-margin">{{ orderDetails.order.delivery_address }}</p>
                  <p class="paragraph-small -no-margin">{{ orderDetails.order.delivery_zip }}</p>
                  <p class="paragraph-small -no-margin">{{ orderDetails.order.delivery_city }}, {{ orderDetails.order.delivery_country }}</p>
                </div>
              </div>
              <div class="d-flex justify-space-between">
                <p class="paragraph-small -no-margin w-70">{{ strapi.Confirmation.YourDetailsTitle }}</p>
                <p class="paragraph-small -no-margin w-30">{{ orderDetails.order.delivery_reference_person }}</p>
              </div>
              <div class="d-flex justify-space-between">
                <p class="paragraph-small -no-margin w-70">{{ strapi.Confirmation.OrderDateTitle }}</p>
                <p class="paragraph-small -no-margin w-30">{{ $utils.formatDateAsDayMonthYear(orderDetails.order.created_at, 'full') }}</p>
              </div>
            </div>
          </header>

          <VDivider class="mt-5" />
          <header class="mt-5 d-flex flex-column gap-16">
            <div class="d-flex align-center mb-5">
              <VIcon icon="fas fa-money-check" size="24" class="mr-3" />
              <p class="paragraph-large -no-margin">{{ strapi.Confirmation.PaymentInformationTitle }}</p>
            </div>
            <div class="pl-9 d-flex flex-column gap-16">
              <div v-if="orderDetails.customerDetails" class="d-flex justify-space-between">
                <p class="paragraph-small -no-margin w-70">{{ strapi.Confirmation.PaymentTermsTitle }}</p>
                <p class="paragraph-small -no-margin w-30">{{ orderDetails.customerDetails.paymentTerms }}</p>
              </div>
              <div v-if="orderDetails.order" class="d-flex justify-space-between">
                <p class="paragraph-small -no-margin w-70">{{ strapi.Confirmation.PaymentMethodTitle }}</p>
                <p class="paragraph-small -no-margin w-30">{{ orderDetails.order.payment_method.name }}</p>
              </div>
            </div>
          </header>
          <VDivider class="mt-5" />
        </Card>
      </div>
    </div>
  </Card>
</template>

<script>
import { VDivider } from 'vuetify/lib/components/index.mjs';
import CartItem from '@Components/CartItem.vue';

export default {
  name: 'Confirmation',
  components: { VDivider, CartItem },
  props: {
    strapi: {
      type: Object,
      required: true
    },
    strapiCheckoutPayment: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      orderDetails: {},
      cartItems: [],
      expandedReceipt: false
    };
  },
  mounted() {
    this.getOrderDetails();
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'checkoutConfirmationStepLoaded'
    });
  },
  methods: {
    async onNavigatedTo() {
      await this.getOrderDetails();
    },
    async getOrderDetails() {
      const response = await this.get('/checkout/get-order-details/', { orderNumber: this.getURLParameter('orderNumber') });
      if (!response) {
        return;
      }
      console.log(response);

      this.orderDetails = response;
      this.expandedReceipt = [0];
      this.cartItems = this.orderDetails.items;
    }
  }
};
</script>
